import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import {
    ArrowDownLeft, ArrowDownRight, Users, Search,
    ChevronDown, ChevronUp,
} from "lucide-react";

function initials(name = "") {
    return name.split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
}

/* ─── stat card with left accent border ─────────────────────────── */
function StatCard({ label, value, sub, accentColor, subColor }) {
    return (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex">
            <div className="w-1.5 shrink-0" style={{ backgroundColor: accentColor }} />
            <div className="p-5 flex flex-col gap-1 min-w-0">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{label}</span>
                <span className="text-4xl font-extrabold text-[#0C2B5C] leading-none">{value}</span>
                <span className="text-xs font-semibold mt-0.5" style={{ color: subColor }}>{sub}</span>
            </div>
        </div>
    );
}

/* ─── wing chip ──────────────────────────────────────────────────── */
function WingChip({ side }) {
    if (side === "L") return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#10B1B6] bg-[#10B1B6]/10 border border-[#10B1B6]/20 px-2.5 py-0.5 rounded-full whitespace-nowrap">
            ✓ Left
        </span>
    );
    if (side === "R") return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#E45B80] bg-[#E45B80]/10 border border-[#E45B80]/20 px-2.5 py-0.5 rounded-full whitespace-nowrap">
            ↘ Right
        </span>
    );
    return <span className="text-slate-300 text-xs">—</span>;
}

/* ─── level badge ────────────────────────────────────────────────── */
const LEVEL_STYLES = [
    "bg-[#0C2B5C] text-white",
    "bg-[#2172E5] text-white",
    "bg-blue-500  text-white",
    "bg-blue-400  text-white",
    "bg-blue-200  text-[#0C2B5C]",
    "bg-blue-100  text-blue-700",
];
function LevelBadge({ depth }) {
    const labels = ["Root", "L1", "L2", "L3", "L4", "L5+"];
    const cls = LEVEL_STYLES[Math.min(depth, LEVEL_STYLES.length - 1)];
    return (
        <span className={`text-[10px] font-black px-2.5 py-1 rounded-full shadow-sm whitespace-nowrap ${cls}`}>
            {labels[Math.min(depth, 5)]}
        </span>
    );
}

/* ─── dual network bar ───────────────────────────────────────────── */
function NetworkBar({ left, right, maxLeft, maxRight }) {
    const lPct = maxLeft  > 0 ? Math.round((left  / maxLeft)  * 100) : 0;
    const rPct = maxRight > 0 ? Math.round((right / maxRight) * 100) : 0;
    return (
        <div className="min-w-[130px]">
            <div className="flex items-center justify-between text-[10px] mb-1.5">
                <span className="text-[#10B1B6] font-bold">{left}L</span>
                <span className="text-[#E45B80] font-bold">{right}R</span>
            </div>
            <div className="space-y-1">
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#10B1B6] rounded-full transition-all" style={{ width: `${lPct}%` }} />
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#E45B80] rounded-full transition-all" style={{ width: `${rPct}%` }} />
                </div>
            </div>
        </div>
    );
}

/* ─── referrer chip ──────────────────────────────────────────────── */
function ReferrerChip({ name, code }) {
    if (!name) return <span className="text-slate-300 text-xs">—</span>;
    return (
        <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-700 truncate">{name}</div>
            <div className="text-[10px] font-mono text-[#10B1B6] font-bold">{code}</div>
        </div>
    );
}

/* ─── sortable th ────────────────────────────────────────────────── */
function SortTh({ col, sortCol, sortAsc, onSort, children, className = "" }) {
    const active = sortCol === col;
    return (
        <th
            onClick={() => onSort(col)}
            className={`py-3 px-3 text-[10px] font-extrabold uppercase tracking-widest cursor-pointer select-none whitespace-nowrap transition-colors
                ${active ? "text-[#0C2B5C]" : "text-slate-400 hover:text-slate-600"} ${className}`}
        >
            <span className="inline-flex items-center gap-1">
                {children}
                {active
                    ? (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
                    : <ChevronDown className="w-3 h-3 opacity-30" />
                }
            </span>
        </th>
    );
}

/* ─── main page ──────────────────────────────────────────────────── */
export default function Team() {
    const { user }  = useAuth();
    const [members,    setMembers]   = useState([]);
    const [loading,    setLoading]   = useState(true);
    const [q,          setQ]         = useState("");
    const [sideFilter, setSide]      = useState("all");
    const [sortCol,    setSortCol]   = useState("depth");
    const [sortAsc,    setSortAsc]   = useState(true);

    useEffect(() => {
        api.get("/users/my-team")
            .then(r => setMembers(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const toggleSort = col => {
        if (sortCol === col) setSortAsc(a => !a);
        else { setSortCol(col); setSortAsc(true); }
    };

    const leftAll  = members.filter(m => m.side === "L");
    const rightAll = members.filter(m => m.side === "R");
    const maxLeft  = Math.max(...members.map(m => m.left_active_count),  1);
    const maxRight = Math.max(...members.map(m => m.right_active_count), 1);

    const filtered = members
        .filter(m => {
            const matchSide = sideFilter === "all" || m.side === sideFilter;
            const matchQ    = !q || [m.full_name, m.email, m.referral_code].some(v => v?.toLowerCase().includes(q.toLowerCase()));
            return matchSide && matchQ;
        })
        .sort((a, b) => {
            let av = a[sortCol] ?? "", bv = b[sortCol] ?? "";
            if (typeof av === "string") av = av.toLowerCase();
            if (typeof bv === "string") bv = bv.toLowerCase();
            return sortAsc ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
        });

    return (
        <AppShell>
            <div className="space-y-5 pb-12">

                {/* ── Header ── */}
                <div className="flex justify-between items-center gap-4">
                    <div>
                        <div className="overline">My Team</div>
                        <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5">
                            Network Overview
                        </h1>
                    </div>
                    {user?.referral_code && (
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 whitespace-nowrap border border-teal-200">
                            Sponsor ID: {user.referral_code}
                        </span>
                    )}
                </div>

                {/* ── 4 Stat Cards ── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        label="Left Wing"
                        value={leftAll.length}
                        sub="active downline sponsors"
                        accentColor="#10B1B6"
                        subColor="#10B1B6"
                    />
                    <StatCard
                        label="Right Wing"
                        value={rightAll.length}
                        sub="active downline sponsors"
                        accentColor="#E45B80"
                        subColor="#E45B80"
                    />
                    <StatCard
                        label="Total Network"
                        value={members.length}
                        sub="all levels combined"
                        accentColor="#2172E5"
                        subColor="#2172E5"
                    />
                    <StatCard
                        label="Direct Members"
                        value={members.filter(m => m.depth === 1).length}
                        sub="level 1 direct referrals"
                        accentColor="#F59E0B"
                        subColor="#F59E0B"
                    />
                </div>

                {/* ── Filter bar ── */}
                <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl shadow-sm px-4 py-3">
                    {/* Wing filter pills */}
                    <div className="flex items-center gap-2">
                        {[
                            { val: "all", label: "All",        count: members.length  },
                            { val: "L",   label: "Left Wing",  count: leftAll.length  },
                            { val: "R",   label: "Right Wing", count: rightAll.length },
                        ].map(({ val, label, count }) => (
                            <button
                                key={val}
                                onClick={() => setSide(val)}
                                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition-all
                                    ${sideFilter === val
                                        ? "bg-[#0C2B5C] text-white shadow-sm"
                                        : "text-slate-500 hover:text-slate-800"
                                    }`}
                            >
                                {label}
                                <span className={`text-[11px] font-black px-1.5 py-0.5 rounded-full min-w-[20px] text-center
                                    ${sideFilter === val ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                                    {count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="relative ml-auto">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Search name, email or ID..."
                            className="h-9 pl-9 pr-4 rounded-full border border-slate-200 bg-white text-xs
                                       focus:outline-none focus:border-[#2172E5] focus:ring-2 focus:ring-blue-100 w-56
                                       placeholder:text-slate-400 transition-colors"
                        />
                    </div>
                </div>

                {/* ── Members table ── */}
                <div className="bg-white rounded-3xl shadow-sm overflow-hidden" data-testid="team-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-slate-100 bg-white">
                                    <th className="text-center py-3 px-4 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest w-10">#</th>
                                    <th className="text-left   py-3 px-4 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Member</th>
                                    <th className="text-center py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Wing</th>
                                    <SortTh col="depth" sortCol={sortCol} sortAsc={sortAsc} onSort={toggleSort} className="text-center">Level</SortTh>
                                    <th className="text-left   py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest whitespace-nowrap">Referred By</th>
                                    <th className="text-left   py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest whitespace-nowrap">Network (L + R)</th>
                                    <th className="text-center py-3 px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Status</th>
                                    <SortTh col="activated_at" sortCol={sortCol} sortAsc={sortAsc} onSort={toggleSort} className="text-right pr-4">Joined</SortTh>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={8}>
                                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                                            <Users className="w-10 h-10 text-slate-200 animate-pulse" />
                                            <span className="text-sm font-medium text-slate-400">Loading your network…</span>
                                        </div>
                                    </td></tr>
                                )}

                                {!loading && filtered.length === 0 && (
                                    <tr><td colSpan={8}>
                                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                                            <Users className="w-10 h-10 text-slate-200" />
                                            <p className="text-sm font-medium text-slate-400">
                                                {members.length === 0 ? "No members in your network yet." : "No members match this filter."}
                                            </p>
                                            {q && <button onClick={() => setQ("")} className="text-xs text-[#2172E5] hover:underline">Clear search</button>}
                                        </div>
                                    </td></tr>
                                )}

                                {filtered.map((m, i) => {
                                    const isLeft   = m.side === "L";
                                    const isActive = m.status === "active";
                                    const rowAccent = isLeft
                                        ? "border-l-4 border-[#10B1B6]"
                                        : "border-l-4 border-[#E45B80]/60";
                                    const evenBg = i % 2 === 0 ? "bg-white" : "bg-slate-50/50";

                                    return (
                                        <tr key={m.id}
                                            className={`transition-all ${evenBg} ${rowAccent} hover:bg-blue-50/40`}>

                                            <td className="py-3.5 px-4 text-center text-xs font-mono font-semibold text-slate-300">
                                                {i + 1}
                                            </td>

                                            <td className="py-3.5 px-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 rounded-full flex-shrink-0 grid place-items-center text-[11px] font-black ring-2
                                                        ${isLeft
                                                            ? "bg-[#10B1B6]/10 text-[#10B1B6] ring-[#10B1B6]/20"
                                                            : "bg-[#E45B80]/10 text-[#E45B80] ring-[#E45B80]/20"
                                                        }`}>
                                                        {initials(m.full_name)}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-bold text-slate-800 truncate text-sm">{m.full_name}</div>
                                                        <div className="text-[11px] text-slate-400 truncate">
                                                            <span className="font-mono font-bold text-slate-500">{m.referral_code}</span>
                                                            {m.email && <> · {m.email}</>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="py-3.5 px-3 text-center">
                                                <WingChip side={m.side} />
                                            </td>

                                            <td className="py-3.5 px-3 text-center">
                                                <LevelBadge depth={m.depth} />
                                            </td>

                                            <td className="py-3.5 px-3">
                                                <ReferrerChip name={m.referrer_name} code={m.referrer_code} />
                                            </td>

                                            <td className="py-3.5 px-3">
                                                <NetworkBar
                                                    left={m.left_active_count}
                                                    right={m.right_active_count}
                                                    maxLeft={maxLeft}
                                                    maxRight={maxRight}
                                                />
                                            </td>

                                            <td className="py-3.5 px-3 text-center">
                                                {isActive ? (
                                                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                                                        <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" /> Active
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500">
                                                        <span className="w-2 h-2 rounded-full bg-slate-300 shrink-0" /> {m.status}
                                                    </span>
                                                )}
                                            </td>

                                            <td className="py-3.5 px-4 text-right text-xs text-slate-500 font-semibold whitespace-nowrap">
                                                {m.activated_at
                                                    ? new Date(m.activated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                                    : <span className="text-slate-200">—</span>
                                                }
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>

                            {members.length > 0 && (
                                <tfoot>
                                    <tr className="border-t border-slate-100 bg-slate-50/60">
                                        <td colSpan={3} className="py-3 px-4 text-xs font-bold text-slate-500">
                                            {members.length} member{members.length !== 1 ? "s" : ""} total
                                        </td>
                                        <td colSpan={2} className="py-3 px-3 text-center">
                                            <div className="flex items-center justify-center gap-4 text-xs">
                                                <span className="flex items-center gap-1 text-[#10B1B6] font-bold">
                                                    <ArrowDownLeft className="w-3 h-3" /> {leftAll.length} Left
                                                </span>
                                                <span className="text-slate-200">·</span>
                                                <span className="flex items-center gap-1 text-[#E45B80] font-bold">
                                                    <ArrowDownRight className="w-3 h-3" /> {rightAll.length} Right
                                                </span>
                                            </div>
                                        </td>
                                        <td colSpan={3} className="py-3 px-4 text-right text-xs text-slate-400 font-semibold">
                                            {members.filter(m => m.status === "active").length} active ·{" "}
                                            {members.filter(m => m.depth === 1).length} direct
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

            </div>
        </AppShell>
    );
}
